import React, { useEffect, useState } from "react";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    TableContainer,
    ClickAwayListener,
    Checkbox,
    styled,
    Switch,
    CircularProgress,
    Tooltip,
    tooltipClasses,
} from "@mui/material";
import Header from '../partials/header';
import LeftSidebar from '../partials/leftSidebar';
import search from "../../assets/images/icon/search.svg"
import "./orders.css"
import { useNavigate } from "react-router-dom";
import { getMethod, putMethod, postMethod } from "../../services/apiService";
import { Link } from "react-router-dom";
import { usePartnerIdContext } from "../../context/partnerIdContext";
import filterIcon from "../../assets/images/icon-searchFilter.svg";
import iconDownArrow from "../../assets/images/icon-dropdown.svg";
import { ToastContext, useToastContext } from "../../context/toastContext";
import { PROFILE_TYPE } from "../../helper/globalConstant";
import { DistributorsList } from "../sharedComponent/DistributorsList";
import { SelectedDistributorPartner } from "../sharedComponent/SelectedDistributorPartner";
import { getUserPriority, getSessionUser } from "../../services/authService";

const AntSwitch = styled(Switch)(({ theme }) => ({
    width: 40, // Slightly smaller width for the switch
    height: 24, // Slightly smaller height for the switch
    padding: 0,
    display: "flex",
    "&:active": {
        "& .MuiSwitch-thumb": {
            width: "14px", // Slightly smaller thumb size
            height: "14px", // Slightly smaller thumb size
        },
    },
    "& .MuiSwitch-switchBase": {
        padding: 4, // Adjusted padding for a smaller thumb
        "&.Mui-checked": {
            transform: "translateX(16px)", // Adjusted translation for smaller switch
            color: "#1F2F34",
            "& .MuiSwitch-thumb": {
                backgroundColor: "#1E2121", // Make the thumb black when enabled
            },
            "& + .MuiSwitch-track": {
                opacity: 1,
                backgroundColor: "#17B2EB",
                border: "1.5px solid #17B2EB", // Thicker border on active state
            },
        },
    },
    "& .MuiSwitch-thumb": {
        width: "14px", // Slightly smaller thumb size
        height: "14px", // Slightly smaller thumb size
        margin: "1px", // Adjusted margin to center the thumb
        borderRadius: "50%", // Ensuring the thumb remains perfectly round
        boxShadow: "none",
        transition: theme.transitions.create(["width"], {
            duration: 300,
        }),
    },
    "& .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: "transparent",
        boxSizing: "border-box",
        border: "1.5px solid var(--text-text-tertiary, #A5A8A9)",
        borderRadius: 24 / 2, // Adjusted border radius for the smaller track
    },
}));

const CustomTooltip = styled(({ className, ...props }: any) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: '#03171F',
        color: '#E2E3E3',
        fontSize: '12px',
        padding: '4px 12px',
        borderRadius: '50px',
    },
    [`& .${tooltipClasses.arrow}`]: {
        color: '#03171F',
    },
}));


export default function OrdersMain() {

    const [allOrders, setAllOrders] = useState<any[]>([]);
    const { partnerId, setPartnerId } = usePartnerIdContext();
    const { setToast } = useToastContext() as ToastContext;
    const [filteredData, setFilteredData] = useState<any[]>([]);
    const [loader, showLoader] = useState(true);

    const [openFilter, setOpenFilter] = useState(false);
    const [userPriority, setUserPriority] = useState<any>(getUserPriority());
    const [activeView, setActiveView] = useState<"distributors" | "orders">("orders");
    const [selectedDistributor, setSelectedDistributor] = useState<any>(null);
    const [distributorListData, setDistributorListData] = useState<any[]>([]);
    const [isLoadingDistributors, setIsLoadingDistributors] = useState(false);
    const [isReady, setIsReady] = useState(false);

    const isPartnerOrder = (userPriority === PROFILE_TYPE.SUPER_ADMIN || userPriority === PROFILE_TYPE.DISTRIBUTOR);

    const filterOptions = isPartnerOrder
        ? [
            { label: "Partner", key: "partnerUser.name" },
            { label: "Order Name", key: "vPartnerOrder.partnerOrderName" },
        ]
        : [
            { label: "Customer", key: "vEnterprise.name" },
            { label: "Site / Devices", key: "vSite.name" },
            { label: "Order Name", key: "vSiteOrder.orderName" },
        ];

    useEffect(() => {
        fetchUserPriority();
    }, []);

    useEffect(() => {
        fetchUserPriority();
    }, []);

    const fetchUserPriority = async () => {
        let priority = getUserPriority();
        setUserPriority(priority);

        if (priority === PROFILE_TYPE.SUPER_ADMIN || priority === PROFILE_TYPE.DISTRIBUTOR) {
            setPartnerId("");
        }

        if (priority === PROFILE_TYPE.SUPER_ADMIN) {
            setActiveView("distributors");
            fetchDistributorsForCAdmin();
        } else {
            setActiveView("orders");
        }
        
        setIsReady(true);
    };

    const fetchDistributorsForCAdmin = async () => {
        setIsLoadingDistributors(true);
        try {
            const response = await getMethod(`partner/users`);
            if (response?.data?.code === 200) {
                setDistributorListData(response.data?.result ?? []);
            }
        } catch (e) {
            console.error("Error fetching distributors", e);
        } finally {
            setIsLoadingDistributors(false);
        }
    };

    const handleSelectDistributor = (distributor: any) => {
        setSelectedDistributor(distributor);
        setPartnerId(distributor.userId);
        setActiveView("orders");
    };

    useEffect(() => {
        const priority = getUserPriority();

        if (priority === PROFILE_TYPE.DISTRIBUTOR) {
            showLoader(true);
            fetchOrderList();
        } else if (partnerId) {
            showLoader(true);
            fetchOrderList();
        } else {
            setAllOrders([]);
            setFilteredData([]);
            showLoader(false);
        }
    }, [partnerId, userPriority]);

    const fetchOrderList = async () => {
        let partnerInfo = "";
        const priority = getUserPriority();
        const sess_user = getSessionUser();
        if (sess_user) {
            const extractedProfileId = sess_user?.userProfile;
            if (extractedProfileId) {
                const priorityFromProfile = extractedProfileId?.priority;
                if (priorityFromProfile == PROFILE_TYPE.INTEGRATOR_TECHNICIAN_G1 || priorityFromProfile == PROFILE_TYPE.INTEGRATOR_TECHNICIAN_G2) {
                    partnerInfo = extractedProfileId?.ownerId;
                } else if (priorityFromProfile == PROFILE_TYPE.DISTRIBUTOR) {
                    partnerInfo = sess_user?.user?.userId;
                } else if (priorityFromProfile == PROFILE_TYPE.SUPER_ADMIN || priorityFromProfile == PROFILE_TYPE.INTEGRATOR) {
                    partnerInfo = partnerId as any;
                }
            } else {
                console.error("Priority id not found in userProfile");
            }
        }

        if (!partnerInfo) {
            setAllOrders([]);
            setFilteredData([]);
            showLoader(false);
            return;
        }

        const isDistributorView = (priority === PROFILE_TYPE.SUPER_ADMIN || priority === PROFILE_TYPE.DISTRIBUTOR);
        const endpoint = isDistributorView
            ? `partner/order/distributorid/${partnerInfo}`
            : `customer/order/partnerid/${partnerInfo}`;

        const resp = await getMethod(endpoint);
        if (resp?.data?.code === 200) {
            setAllOrders(resp?.data?.result || []);
            setFilteredData(resp?.data?.result || []);
            showLoader(false);
        } else {
            setAllOrders([]);
            setFilteredData([]);
            showLoader(false);
        }
    };

    const navigate = useNavigate();

    const handleRowClick = (order: any) => {
        navigate("/orders/details", { state: { order } });
    };


    function convertTimestampToDate(timestamp: number): string {
        const formattedDate = new Date(timestamp).toLocaleString();
        return formattedDate.replace(',', '  |');
    }

    const [searchText, setSearchText] = useState("");
    const [selectedFilter, setSelectedFilter] = useState<string | null>(null);

    const getNestedValue = (obj: any, path: string) => {
        if (path === "partnerUser.name") {
            const partnerUser = obj?.partnerUser || obj?.vPartnerUser;
            return partnerUser ? `${partnerUser.name || ""} ${partnerUser.lastName || ""}`.trim() : obj?.vPartnerOrder?.partnerId || "";
        }
        return path.split(".").reduce((acc, key) => acc?.[key], obj);
    };

    useEffect(() => {
        let result = [...allOrders];

        if (searchText) {
            const trimmedSearchText = searchText.trim();
            if (trimmedSearchText) {
                const lowerSearchText = trimmedSearchText.toLowerCase();
                result = result.filter((item: any) => {
                    if (isPartnerOrder) {
                        const partnerUser = item?.partnerUser || item?.vPartnerUser;
                        const partnerName = (partnerUser ? `${partnerUser.name || ""} ${partnerUser.lastName || ""}`.trim() : item?.vPartnerOrder?.partnerId || "")?.toLowerCase();
                        const partnerEmail = (item?.vPartnerOrder?.partnerId || "")?.toLowerCase();
                        const partnerOrderName = (item?.vPartnerOrder?.partnerOrderName || "")?.toLowerCase();
                        return (
                            partnerName.includes(lowerSearchText) ||
                            partnerEmail.includes(lowerSearchText) ||
                            partnerOrderName.includes(lowerSearchText)
                        );
                    } else {
                        const enterpriseName = (item?.vEnterprise?.name || "")?.toLowerCase();
                        const siteName = (item?.vSite?.name || "")?.toLowerCase();
                        const centralUserEmail = (item?.vCentralUsers?.[0]?.mailId || "")?.toLowerCase();
                        const siteOrderName = (item?.vSiteOrder?.orderName || "")?.toLowerCase();
                        const licenseName = (item?.license?.data?.name || "")?.toLowerCase();

                        return (
                            enterpriseName.includes(lowerSearchText) ||
                            siteName.includes(lowerSearchText) ||
                            centralUserEmail.includes(lowerSearchText) ||
                            siteOrderName.includes(lowerSearchText) ||
                            licenseName.includes(lowerSearchText)
                        );
                    }
                });
            }
        }

        if (selectedFilter) {
            result.sort((a: any, b: any) => {
                const valueA = getNestedValue(a, selectedFilter)?.toLowerCase() || "";
                const valueB = getNestedValue(b, selectedFilter)?.toLowerCase() || "";
                return valueA.localeCompare(valueB);
            });
        }

        setFilteredData(result);
    }, [allOrders, searchText, selectedFilter, isPartnerOrder]);

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(e.target.value);
    };

    const handleSortChange = (option: string) => {
        const newFilter = option === selectedFilter ? null : option;
        setSelectedFilter(newFilter);
    };


    const handleOrderStatus = async (orderId: string, new_status: any) => {
        if (orderId) {
            let resp;
            if (userPriority === PROFILE_TYPE.DISTRIBUTOR) {
                const payload = {
                    partnerOrderId: orderId,
                    status: new_status,
                };
                resp = await postMethod('partner/order/update/status', payload);
            } else {
                const payload = {
                    orderId: orderId,
                    status: new_status,
                };
                resp = isPartnerOrder
                    ? await postMethod('partner/order/update', payload)
                    : await putMethod('customer/order/status', payload);
            }

            if (resp?.data?.code === 200 || resp?.status === 200) {
                const statusKey = isPartnerOrder ? "vPartnerOrder" : "vSiteOrder";
                setAllOrders((prevOrders: any[]) =>
                    prevOrders.map((item) => {
                        const targetId = item?.[statusKey]?.partnerOrderId || item?.[statusKey]?.orderId;
                        return targetId === orderId
                            ? {
                                ...item,
                                [statusKey]: {
                                    ...item[statusKey],
                                    status: new_status,
                                },
                            }
                            : item;
                    })
                );
                setFilteredData((prevOrders: any[]) =>
                    prevOrders.map((item) => {
                        const targetId = item?.[statusKey]?.partnerOrderId || item?.[statusKey]?.orderId;
                        return targetId === orderId
                            ? {
                                ...item,
                                [statusKey]: {
                                    ...item[statusKey],
                                    status: new_status,
                                },
                            }
                            : item;
                    })
                );
                setToast("Order status updated successfully!", "success");
            } else {
                setToast("Failed to update the order status!", "error");
            }
        }
    };


    const getBreadcrumbs = () => {
        const isCAdmin = userPriority === PROFILE_TYPE.SUPER_ADMIN;
        const items = [];

        if (!isCAdmin) return null;

        items.push(
            <span
                key="bc-dist"
                className={`bc-item ${activeView === "distributors" ? "active" : "inactive"}`}
                style={{
                    cursor: activeView !== "distributors" ? "pointer" : "default",
                }}
                onClick={() => {
                    setActiveView("distributors");
                    setSelectedDistributor(null);
                    setPartnerId("");
                }}
            >
                Distributors
            </span>,
        );
        if (activeView === "orders") {
            items.push(
                <span key="sep1" className="bc-sep">
                    ›
                </span>,
            );
            items.push(
                <span key="bc-ord" className="bc-item active">
                    Orders
                </span>,
            );
        }

        return (
            <Box
                sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "14px 24px 14px 100px",
                    bgcolor: "#1f2f34",
                    borderBottom: "1px solid #17B2EB22",
                }}
            >
                {items}
            </Box>
        );
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>

            <Header title="All Orders" breadCrumbBar={getBreadcrumbs()} />
            <Box sx={{ display: 'flex' }}>
                <LeftSidebar />

                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        p: 3,
                        marginTop: userPriority === PROFILE_TYPE.SUPER_ADMIN ? 14 : 8,
                        padding: '0',
                    }}
                >
                    <Box
                        p={3}
                        sx={{
                            backgroundColor: "#03171F",
                            minHeight: "calc(100vh - 112px)",
                        }}
                    >
                        {isReady && (
                            <>
                                <Box sx={{ mb: activeView === "orders" ? 3 : 0 }}>
                                    <SelectedDistributorPartner
                                        selectedDistributor={selectedDistributor}
                                    />
                                </Box>

                                {activeView === "distributors" && (
                                    <DistributorsList
                                        distributors={distributorListData}
                                        isLoading={isLoadingDistributors}
                                        onSelect={handleSelectDistributor}
                                    />
                                )}

                                {activeView === "orders" && (
                                    <>
                                        <Box sx={{ display: "flex", alignContent: "space-between", justifyContent: "space-between", gap: "32px", padding: "1rem 0" }}>
                                            <div
                                                style={{
                                                    width: 468,
                                                    height: 41,
                                                    padding: 12,
                                                    borderRadius: 30,
                                                    border: "1px solid #5a5f60",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    background: "#1F2F34",
                                                }}
                                            >
                                                <div style={{ display: "flex", alignItems: "center" }}>
                                                    <img
                                                        src={search}
                                                        alt="Search Icon"
                                                        style={{ cursor: "pointer" }}
                                                    />
                                                    <input
                                                        onChange={handleSearch}
                                                        placeholder={
                                                            (userPriority === PROFILE_TYPE.SUPER_ADMIN || userPriority === PROFILE_TYPE.DISTRIBUTOR)
                                                                ? "Search by partner name, email ID, order name"
                                                                : "Search by customer name, site / device name, email ID, order name"
                                                        }
                                                        style={{
                                                            background: "#1F2F34",
                                                            border: "none",
                                                            padding: 0,
                                                            margin: 0,
                                                            marginLeft: 8,
                                                            color: "#FFF",
                                                            width: 410,
                                                        }}
                                                    />
                                                </div>
                                            </div>

                                            <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
                                                <Box position="relative" display="inline-block">
                                                    <Box
                                                        onClick={() => setOpenFilter(!openFilter)}
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: "4px",
                                                            cursor: "pointer",
                                                            background: "#1F2F34",
                                                            borderRadius: 15,
                                                            width: 60,
                                                            height: 40,
                                                            padding: "8px",
                                                        }}
                                                    >
                                                        <Box position="relative" display="flex">
                                                            <img src={filterIcon} alt="Filter Icon" style={{ marginLeft: "8px" }} />
                                                            {selectedFilter && (
                                                                <span
                                                                    style={{
                                                                        position: "absolute",
                                                                        top: "1px",
                                                                        right: 0,
                                                                        background: "#0699CE",
                                                                        width: "7px",
                                                                        height: "7px",
                                                                        borderRadius: "50%",
                                                                    }}
                                                                ></span>
                                                            )}
                                                        </Box>
                                                        <img src={iconDownArrow} alt="Down Arrow" style={{ marginRight: "8px" }} />
                                                    </Box>

                                                    {/* Dropdown Menu */}
                                                    {openFilter && (
                                                        <ClickAwayListener onClickAway={() => setOpenFilter(false)}>
                                                            <Box
                                                                sx={{
                                                                    position: "absolute",
                                                                    top: "50px",
                                                                    right: "0",
                                                                    background: "#1F2F34",
                                                                    borderRadius: "8px",
                                                                    boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
                                                                    padding: "10px",
                                                                    width: "150px",
                                                                    zIndex: 1000,
                                                                }}
                                                            >
                                                                {filterOptions
                                                                    ?.filter(option => {
                                                                        if (userPriority === PROFILE_TYPE.SUPER_ADMIN || userPriority === PROFILE_TYPE.DISTRIBUTOR) {
                                                                            return option.key !== "vSite.name";
                                                                        }
                                                                        return true;
                                                                    })
                                                                    ?.map((option, index) => (
                                                                        <Box key={index} display="flex" alignItems="center">
                                                                            <Checkbox
                                                                                checked={selectedFilter === option.key}
                                                                                onChange={() => handleSortChange(option.key)}
                                                                                sx={{ color: "#B4B7B8" }}
                                                                            />
                                                                            <span style={{ fontSize: "14px" }}>
                                                                                {(userPriority === PROFILE_TYPE.SUPER_ADMIN || userPriority === PROFILE_TYPE.DISTRIBUTOR) && option.label === "Customer"
                                                                                    ? "Partner"
                                                                                    : option.label
                                                                                }
                                                                            </span>
                                                                        </Box>
                                                                    ))}
                                                            </Box>
                                                        </ClickAwayListener>
                                                    )}
                                                </Box>

                                                {userPriority !== PROFILE_TYPE.SUPER_ADMIN && (
                                                    <Link to="/orders/create" style={{
                                                        textDecoration: "none",
                                                        color: "inherit",
                                                    }}>
                                                        <button className="add-btn" style={{ cursor: "pointer" }}>
                                                            {userPriority === PROFILE_TYPE.DISTRIBUTOR ? "Add New Partner Order" : "Add New Site Order"}
                                                        </button>
                                                    </Link>
                                                )}
                                            </Box>
                                        </Box>

                                        <TableContainer component={Paper} sx={{ backgroundColor: "#03171f", padding: "1rem 0" }}>
                                            <Table sx={{ backgroundColor: "#03171f", border: "1px solid #313838" }}>
                                                {/* Table Header */}
                                                <TableHead>
                                                    <TableRow style={{ borderBottom: "2px solid #313838" }}>
                                                        <TableCell sx={{ color: "#E2E3E3", borderColor: "#313838", fontWeight: "500", fontSize: "16px" }}>Created On</TableCell>
                                                        {(userPriority === PROFILE_TYPE.SUPER_ADMIN || userPriority === PROFILE_TYPE.DISTRIBUTOR) ? (
                                                            <>
                                                                <TableCell sx={{ color: "#E2E3E3", borderColor: "#313838", fontWeight: "500", fontSize: "16px" }}>Partner</TableCell>
                                                                <TableCell sx={{ color: "#E2E3E3", borderColor: "#313838", fontWeight: "500", fontSize: "16px" }}>Partner Email</TableCell>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <TableCell sx={{ color: "#E2E3E3", borderColor: "#313838", fontWeight: "500", fontSize: "16px" }}>Customer</TableCell>
                                                                <TableCell sx={{ color: "#E2E3E3", borderColor: "#313838", fontWeight: "500", fontSize: "16px" }}>Site / Devices</TableCell>
                                                                <TableCell sx={{ color: "#E2E3E3", borderColor: "#313838", fontWeight: "500", fontSize: "16px" }}>Customer Email</TableCell>
                                                            </>
                                                        )}
                                                        <TableCell sx={{ color: "#E2E3E3", borderColor: "#313838", fontWeight: "500", fontSize: "16px" }}>Order Name</TableCell>
                                                        <TableCell sx={{ color: "#E2E3E3", borderColor: "#313838", fontWeight: "500", fontSize: "16px" }}>Status</TableCell>
                                                    </TableRow>
                                                </TableHead>

                                                {/* Table Body */}
                                                <TableBody>
                                                    {filteredData?.length > 0 ? (
                                                        filteredData?.map((order: any, index) => {
                                                            const orderDetail = isPartnerOrder ? order?.vPartnerOrder : order?.vSiteOrder;
                                                            const isActive = isPartnerOrder ? (orderDetail?.status === 0) : (orderDetail?.status === 1);
                                                            const createdTime = orderDetail?.createdAt;
                                                            const orderName = isPartnerOrder ? orderDetail?.partnerOrderName : orderDetail?.orderName;
                                                            const orderId = orderDetail?.partnerOrderId || orderDetail?.orderId;
                                                            const partnerUser = order?.partnerUser || order?.vPartnerUser;
                                                            const partnerName = partnerUser ? `${partnerUser.name || ""} ${partnerUser.lastName || ""}`.trim() : orderDetail?.partnerId;
                                                            const partnerEmail = orderDetail?.partnerId;

                                                            return (
                                                                <TableRow
                                                                    key={index}
                                                                    sx={{ padding: 0, cursor: isActive ? "pointer" : "not-allowed" }}
                                                                    onClick={() => {
                                                                        if (isActive) {
                                                                            handleRowClick(order);
                                                                        }
                                                                    }}
                                                                >
                                                                    <TableCell sx={{ color: "#B4B7B8", borderColor: "#313838" }}><p>{convertTimestampToDate(createdTime)}</p> </TableCell>

                                                                    {isPartnerOrder ? (
                                                                        <>
                                                                            <TableCell sx={{ color: "#B4B7B8", borderColor: "#313838" }}>{partnerName}</TableCell>
                                                                            <TableCell sx={{ color: "#B4B7B8", borderColor: "#313838" }}>{partnerEmail}</TableCell>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <TableCell sx={{ color: "#B4B7B8", borderColor: "#313838" }}>{order?.vEnterprise?.name}</TableCell>
                                                                            <TableCell sx={{ color: "#B4B7B8", borderColor: "#313838" }}>{order?.vSite?.name}</TableCell>
                                                                            <TableCell sx={{ color: "#B4B7B8", borderColor: "#313838" }}>{order?.vCentralUsers?.[0]?.mailId}</TableCell>
                                                                        </>
                                                                    )}

                                                                    <TableCell sx={{ color: "#B4B7B8", borderColor: "#313838" }}>
                                                                        <CustomTooltip title={orderName} arrow>
                                                                            <div style={{
                                                                                maxWidth: "180px",
                                                                                overflow: "hidden",
                                                                                textOverflow: "ellipsis",
                                                                                whiteSpace: "nowrap"
                                                                            }}>
                                                                                {orderName}
                                                                            </div>
                                                                        </CustomTooltip>
                                                                    </TableCell>
                                                                    <TableCell
                                                                        sx={{
                                                                            color: "#B4B7B8",
                                                                            borderColor: "#313838",
                                                                            cursor: userPriority === PROFILE_TYPE.SUPER_ADMIN ? "default" : "pointer"
                                                                        }}
                                                                        onClick={(e: any) => {
                                                                            if (userPriority === PROFILE_TYPE.SUPER_ADMIN) {
                                                                                e.stopPropagation();
                                                                            }
                                                                        }}
                                                                    >
                                                                        {userPriority === PROFILE_TYPE.SUPER_ADMIN ? (
                                                                            isActive ? (
                                                                                <span style={{ cursor: "default", color: "#4CAF50", backgroundColor: "rgba(76, 175, 80, 0.1)", padding: "6px 16px", borderRadius: "20px", fontSize: "13px", fontWeight: 600 }}>Active</span>
                                                                            ) : (
                                                                                <span style={{ cursor: "default", color: "#FFA726", backgroundColor: "rgba(255, 167, 38, 0.1)", padding: "6px 16px", borderRadius: "20px", fontSize: "13px", fontWeight: 600 }}>Suspended</span>
                                                                            )
                                                                        ) : (
                                                                            <AntSwitch
                                                                                checked={isActive}
                                                                                inputProps={{ "aria-label": "ant design" }}
                                                                                onClick={(e: any) => {
                                                                                    e.stopPropagation();
                                                                                    const newStatus = isPartnerOrder ? (e.target.checked ? 0 : 1) : (e.target.checked ? 1 : 3);
                                                                                    handleOrderStatus(orderId, newStatus);
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })
                                                    ) : (
                                                        <TableRow sx={{ background: '#192529' }}>
                                                            <TableCell
                                                                sx={{ color: '#E2E3E3', borderColor: '#313838', margin: '0', textAlign: 'center' }}
                                                                colSpan={(userPriority === PROFILE_TYPE.SUPER_ADMIN || userPriority === PROFILE_TYPE.DISTRIBUTOR) ? 5 : 6}
                                                            >
                                                                {loader ? (
                                                                    <CircularProgress color="primary" />
                                                                ) : (
                                                                    "No Data!"
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </>
                                )}
                            </>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
